'use client';

import { T } from '@/i18n/LocaleProvider';
import { useEffect, useId, useRef, useState } from 'react';
import Link from 'next/link';
import styles from './Navbar.module.scss';

// Menú desplegable del topbar (Producto, Recursos) (patrón disclosure: botón con aria-expanded, no role=menu).
// Los links están siempre en el HTML —aunque el panel esté cerrado— para que los buscadores los rastreen;
// cerrado, el panel va con visibility:hidden, así que sus links no reciben foco con el teclado.
export default function NavDropdown({ label, links, pathname }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const panelId = useId();
  const isCurrent = (href) =>
    pathname === href || (href !== '/' && pathname.startsWith(`${href}/`));
  const active = links.some((l) => isCurrent(l.href));

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      setOpen(false);
      btnRef.current?.focus();
    }
  };

  // Si el foco sale del bloque (Tab hacia otro elemento), se cierra.
  const onBlur = (e) => {
    if (!rootRef.current?.contains(e.relatedTarget)) setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={styles.dropdown}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    >
      <button
        ref={btnRef}
        type="button"
        className={`${styles.dropdownBtn} ${open ? styles.dropdownOpen : ''} ${active ? styles.active : ''}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <T>{label}</T>
        {active && <span className={styles.indicator} aria-hidden="true" />}
      </button>
      <ul
        id={panelId}
        className={`${styles.panel} ${open ? styles.panelOpen : ''}`}
      >
        {links.map(({ label, href }) => (
          <li key={href}>
            <Link href={href} className={isCurrent(href) ? styles.active : ''}>
              <T>{label}</T>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
